import React, { useMemo } from 'react';

import { Button } from '@/design-system/button';
import { SearchSelect } from '@/design-system/search-select';

import { useActivateOnFocus } from '../hooks/useActivateOnFocus';
import {
  type StorageLabels,
  storageLabel,
  useStorageLabels,
  useStoragePicker,
} from '../hooks/useStorages';
import { STORAGE_CATEGORY_LABEL } from '../status';
import type {
  StorageCategory,
  StorageStatus,
  WarehouseStorage,
} from '../types';
import { type PickerA11yProps, usePickerA11y } from './pickerA11y';

const NO_IDS: readonly string[] = [];

interface StorageSelectProps extends PickerA11yProps {
  hubID: number | null;
  /** Chosen storage id ('' = none). */
  value: string;
  onChange: (id: string, storage: WarehouseStorage) => void;
  /** Only storages of this status ('ACTIVE' by default; null = every status). */
  status?: StorageStatus | null;
  /** Only storages of this level (Location / Section / Bin). */
  category?: StorageCategory;
  /**
   * Only the DIRECT children of this storage (a Location's Sections). Passed
   * as '' the parent is not chosen yet: the picker stays idle.
   */
  parentID?: string;
  /**
   * Storage ids left out of the options (the other end of a transfer). The
   * chosen `value` stays listed even when it is excluded.
   */
  excludeIds?: readonly string[];
  /**
   * Offers clearing the chosen storage (filters): the clear button calls this
   * instead of `onChange`.
   */
  onClear?: () => void;
  /** Option and chosen label (default: `Location / Section / Name (Level, Type)`). */
  labelOf?: (s: WarehouseStorage) => string;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  testId?: string;
  /**
   * Labels the parent already resolves for all its chosen ids (one `ids=`
   * request for a whole form). Without it the picker resolves its own value.
   */
  labels?: StorageLabels;
}

/**
 * Server-searched, paged storage picker of one hub (any level, any type —
 * or one `category`, or the children of one `parentID`): the first page once
 * the user reaches it, `?search=` debounced (the name, own or an ancestor's),
 * the next page when the list is scrolled to its end. The chosen id's label
 * comes from `ids=`, not from a page it may not be in. Never loads the whole
 * hub.
 */
export const StorageSelect: React.FC<StorageSelectProps> = ({
  hubID,
  value,
  onChange,
  status = 'ACTIVE',
  category,
  parentID,
  excludeIds = NO_IDS,
  onClear,
  labelOf = storageLabel,
  disabled,
  error,
  placeholder = 'Pilih storage',
  testId,
  labels,
  'aria-label': ariaLabel,
}) => {
  const ref = usePickerA11y(ariaLabel, error);
  // Queries only once the user reaches the picker (focus / click).
  const { active, onFocus } = useActivateOnFocus();
  const waitsForParent = parentID !== undefined && !parentID;
  const picker = useStoragePicker({
    hubID,
    status,
    category,
    parentID,
    enabled: active && !disabled && !waitsForParent,
  });

  const ownIds = useMemo(() => (value ? [value] : NO_IDS), [value]);
  const own = useStorageLabels(hubID, ownIds, !labels);
  const lookup = labels ?? own;

  const chosen = value
    ? (lookup.byID.get(value) ?? picker.rows.find((s) => s.id === value))
    : undefined;
  const options = useMemo(() => {
    const rows = picker.rows
      .filter((s) => s.id === value || !excludeIds.includes(s.id))
      .map((s) => ({ value: s.id, label: labelOf(s) }));
    // The kit only offers "clear" while the chosen row is among the options.
    return onClear &&
      chosen &&
      !picker.searching &&
      !picker.rows.some((s) => s.id === chosen.id)
      ? [{ value: chosen.id, label: labelOf(chosen) }, ...rows]
      : rows;
  }, [picker.rows, picker.searching, chosen, onClear, labelOf, value, excludeIds]);
  const noun = category ? STORAGE_CATEGORY_LABEL[category] : 'Storage';
  // An id is never shown: the label, or what is going on until there is one.
  const chosenLabel = chosen
    ? labelOf(chosen)
    : value
      ? lookup.missing.has(value)
        ? `${noun} tidak ditemukan`
        : lookup.error
          ? 'Gagal memuat'
          : 'Memuat…'
      : undefined;

  return (
    <div ref={ref} className="min-w-0" data-testid={testId} onFocus={onFocus}>
      <SearchSelect
        options={options}
        value={value}
        selectedLabelFallback={chosenLabel}
        onChange={(id) => {
          if (!id) {
            // The kit's clear button.
            onClear?.();
            picker.setSearch('');
            return;
          }
          const row =
            picker.rows.find((s) => s.id === id) ??
            (chosen?.id === id ? chosen : undefined);
          if (!row) return;
          lookup.remember([row]);
          onChange(row.id, row);
          // Back to the first page for the next time the list opens.
          picker.setSearch('');
        }}
        onSearchChange={picker.setSearch}
        disableLocalFilter
        hasMore={picker.hasMore}
        onLoadMore={picker.loadMore}
        loading={picker.loading}
        placeholder={placeholder}
        allowClear={!!onClear}
        disabled={disabled}
        error={error}
        emptyMessage={
          picker.error
            ? `Gagal memuat ${noun.toLowerCase()}: ${picker.error.message}`
            : !hubID
              ? 'Pilih hub dulu'
              : picker.searching
                ? `${noun} tidak ditemukan`
                : status === 'ACTIVE'
                  ? `Belum ada ${noun.toLowerCase()} aktif`
                  : `Belum ada ${noun.toLowerCase()}`
        }
      />
      {picker.error && !disabled && (
        // Unknown ≠ none: say the list failed and offer a retry.
        <div
          className="mt-1 flex flex-wrap items-center gap-1"
          data-testid="storage-select-error"
        >
          <span className="text-[11px] text-(--state-error-base)">
            {`Gagal memuat ${noun.toLowerCase()}.`}
          </span>
          <Button
            tone="neutral"
            style="link"
            size="xs"
            className="h-auto px-0 py-0 text-[11px]"
            onClick={picker.reload}
          >
            Coba lagi
          </Button>
        </div>
      )}
    </div>
  );
};
